import moment from "moment";



class ObservationTime {
  constructor(time) {
    this.time = moment(time);
  }
  format_brief() {
    return this.time.format("ddd MMM Do - h:mma");
  }
}

class InvalidTime {
  format_brief() {
    return '--:--';
  }
}

class TemperatureObservation {
  constructor(value, units) {
    this.value = value;
    this.units = units;
  }
  format_value() {
    return `${this.value}\u00B0`;
  }
  format_units() {
    return this.units;
  }
  format_detail() {
    return `${this.format_value()} ${this.format_units()}`;
  }
  is_valid() {
    return true;
  }
}

class InvalidTemperatureObservation {
  constructor() {}

  format_value() {
    return '--';
  }
  format_units() {
    return '';
  }
  format_detail() {
    return 'Invalid temperature reading';
  }
  is_valid() {
    return false;
  }
}

class RainObservation {
  constructor(value) {
    this.value = value
  }
  format_value() {
    const amount = (parseFloat(this.value) / 100).toFixed(2);
    return `${amount}"`;
  }
  format_units() {
    return `"`;
  }
  format_detail() {
    return `${this.format_value()} of rain previous 24 hours`;
  }
  is_valid() {
    return true;
  }
}

class WindObservation {
  constructor(value, direction_degrees, units) {
    this.value = value;
    this.direction = direction_degrees;
    this.units = units;
  }
  format_value() {
    return `${this.value}`;
  }
  format_direction() {
    if (isNaN(parseFloat(this.direction)) || this.value < 1) {
      return '--';
    }

    let degrees = parseFloat(this.direction);

    const lookups = [
      {deg: 0, disp: 'N',},
      {deg: 22.5, disp: 'NNE',},
      {deg: 45, disp: 'NE',},
      {deg: 67.5, disp: 'ENE',},
      {deg: 90, disp: 'E',},
      {deg: 112.5, disp: 'ESE',},
      {deg: 135, disp: 'SE',},
      {deg: 157.5, disp: 'SSE',},
      {deg: 180, disp: 'S',},
      {deg: 202.5, disp: 'SSW',},
      {deg: 225, disp: 'SW',},
      {deg: 247.5, disp: 'WSW',},
      {deg: 270, disp: 'W',},
      {deg: 292.5, disp: 'WNW',},
      {deg: 315, disp: 'NW',},
      {deg: 337.5, disp: 'NNW',},
    ];

    let match = lookups.find(({deg, disp}) => {
      return (degrees >= (deg-11.25) && degrees <= (deg+11.25));
    });
    if (match) {
      return match.disp;
    }

    return '--';
  }
  format_detail() {
    if (this.format_direction() === '--') {
      return `${this.format_value()}`;
    }
    return `${this.format_value} mph from the ${this.format_units}`;
  }
  format_units() {
    return 'mph';
  }
  is_valid() {
    return true;
  }
}


class InvalidWindObservation {

  format_value() {
    return '--';
  }
  format_units() {
    return '';
  }
  format_direction() {
    return '--'
  }
  format_detail() {
    return "No wind data";
  }
  is_valid() {
    return false;
  }
}

class HumidityObservation {
  constructor(value) {
    this.value = value;
  }
  format_value() {
    return `${this.value}`;
  }
  format_units() {
    return '%';
  }
  format_detail() {
    return `${this.format_value()}${this.format_units()}`;
  }
  is_valid() {
    return true;
  }
}


class PressureObservation {
  constructor(value) {
    this.value = value;
  }
  format_value() {
    return parseFloat(this.value).toFixed(2);
  }
  format_units() {
    return 'inHg';
  }
  format_detail() {
    return `${this.format_value()} ${this.format_units()}`;
  }
  is_valid() {
    return true;
  }
}

class SolarObservation {
  constructor(value) {
    this.value = value;
  }
  format_value() {
    return `${this.value}`;
  }
  format_units() {
    return 'W/m<sup>2</sup>';
  }
  format_detail() {
    return `${this.format_value()} ${this.format_units()}`;
  }
  is_valid() {
    return true;
  }
}

class SoilMoistureObservation {
  constructor(value, index) {
    this.value = value;
    this.depth = indexToSoilDepth(index);
  }
  get_num_drops() {
    if (this.value >= 100) {
      return 1;
    } else if (this.value >= 60) {
      return 2;
    } else if (this.value >= 30) {
      return 3;
    } else if (this.value >= 10) {
      return 4;
    } else {
      return 5;
    }
  }
  format_value() {
    return `${this.value}`;
  }
  format_units() {
    return 'cb';
  }
  format_depth() {
    return `${this.depth.format()}`;
  }
  format_detail() {
    return `${this.format_value()}${this.format_units()} at ${this.format_depth()}`;
  }
  is_valid() {
    return true;
  }
}

class InvalidMoistureObservation {
  constructor(index) {
    this.depth = indexToSoilDepth(index);
  }
  format_value() {
    return '--';
  }
  format_units() {
    return '';
  }
  format_detail() {
    return `No moisture data for ${this.depth.format()}`;
  }
  is_valid() {
    return false;
  }
}

function indexToSoilDepth(index) {
  let depth = null;
  if (index === 1) {
    depth = 2;
  } else if (index === 2) {
    depth = 5;
  } else if (index === 3) {
    depth = 10;
  } else if (index === 4) {
    depth = 15;
  }

  if (depth !== null) {
    return new SoilReadingDepth(depth);
  } else {
    return new InvalidSoilReadingDepth();
  }
}

class SoilReadingDepth {
  constructor(depth) {
    this.depth = depth;
  }
  format() {
    return `${this.depth}"`;
  }
}
class InvalidSoilReadingDepth {
  format() {
    return '';
  }
}


class InvalidObservation {
  constructor(detail_message) {
    this.detail_message = detail_message;
  }
  format_value() {
    return '--';
  }
  format_units() {
    return '';
  }
  format_detail() {
    return this.detail_message;
  }
  is_valid() {
    return false;
  }
}



class WeatherStationObservationParser {
  constructor(data) {
    this.data = data;
  }

  parse_observation_time() {
    return this.data.observation_time !== null ? new ObservationTime(this.data.observation_time) : new InvalidTime();
  }

  parse_current_temperature() {
    return this._parse_temp(this.data.temp);
  }

  parse_current_rain() {
    if (this.data.rain_last_24_hours !== null && parseInt(this.data.rain_last_24_hours) >= 0) {
      return new RainObservation(this.data.rain_last_24_hours);
    } else {
      return new InvalidObservation("No rain data");
    }
  }

  parse_current_wind() {
    if (this.data.wind_speed_mph !== null && this.data.wind_dir_deg !== null) {
      return new WindObservation(this.data.wind_speed_mph, this.data.wind_dir_deg, 'mph');
    } else {
      return new InvalidWindObservation();
    }
  }

  parse_current_humidity() {
    if (this.data.humidity !== null && parseFloat(this.data.humidity) >= 0.0 && parseFloat(this.data.humidity) <= 100.0) {
      return new HumidityObservation(this.data.humidity);
    } else {
      return new InvalidObservation("No humidity data");
    }
  }

  parse_current_pressure() {
    if (this.data.pressure_in_hg !== null) {
      return new PressureObservation(this.data.pressure_in_hg);
    } else {
      return new InvalidObservation("No pressure data");
    }
  }

  parse_current_solar() {
    if (this.data.solar_radiation !== null) {
      return new SolarObservation(this.data.solar_radiation);
    } else {
      return new InvalidObservation("No Solar radiation data");
    }
  }

  parse_soil_temp(index) {
    let val = this.data[`soil_temp_${index}`];
    return this._parse_temp(val);
  }

  parse_soil_moist(index) {
    let val = this.data[`soil_moist_${index}`];
    if (val !== null) {
      return new SoilMoistureObservation(val, index);
    } else {
      return new InvalidMoistureObservation(index);
    }
  }



  _parse_temp(value) {
    if (value !== null && value > -90) {
      return new TemperatureObservation(value, 'F');
    } else {
      return new InvalidTemperatureObservation();
    }
  }
}


export default WeatherStationObservationParser;